import * as React from 'react'

import { intervalToDuration } from 'date-fns'

const pad = (n = 0): string => String(n).padStart(2, '0')

/**
 * Key used to store the total engagement time in localStorage
 */
export const SESSION_TIME_KEY = 'comments_session_active_time'

/**
 * Key used to store the last active timestamp in localStorage
 */
export const LAST_ACTIVE_KEY = 'comments_session_last_active'

/**
 * Event name for when engagement starts
 */
export const ENGAGEMENT_START_EVENT = 'comments_engagement_started'

/**
 * Event name for when engagement stops
 */
export const ENGAGEMENT_STOP_EVENT = 'comments_engagement_stopped'

/**
 * Time in milliseconds before a session is considered inactive
 * If no interaction occurs within this time, the session will reset
 */
const INACTIVITY_THRESHOLD_MS = 3 * 60 * 1000 // 3 minutes

/**
 * Target engagement time in milliseconds
 * Once this time is reached, isActivelyEngaging will be set to true
 */
const TARGET_ENGAGEMENT_MS = 15 * 60 * 1000 // 15 minutes

// For testing
// const INACTIVITY_THRESHOLD_MS = 1 * 60 * 1000 // 1 minute
// const TARGET_ENGAGEMENT_MS = 5 * 1000 // 5 seconds

/**
 * Helper function to trigger the engagement start event
 * This should be called when the user starts or resumes interacting with comments
 */
export const triggerEngagementStart = (): void => {
  window.dispatchEvent(new Event(ENGAGEMENT_START_EVENT))
}

/**
 * Helper function to trigger the engagement stop event
 * This should be called when the user explicitly stops engaging with comments
 */
export const triggerEngagementStop = (): void => {
  window.dispatchEvent(new Event(ENGAGEMENT_STOP_EVENT))
}

/**
 * Hook to track user engagement time with comments
 *
 * Features:
 * - Tracks total time spent engaging with comments
 * - Persists engagement time across page reloads
 * - Resets after 3 minutes of inactivity
 * - Considers user actively engaging after 15 minutes
 *
 * @returns {Object} Engagement timer state
 * @returns {boolean} isActivelyEngaging - True if user has engaged for target time
 * @returns {string} activeTime - Formatted time string (HH:MM:SS)
 *
 * @example
 * const { isActivelyEngaging, activeTime } = useEngagementTimer()
 */
export const useEngagementTimer = (): {
  isActivelyEngaging: boolean
  activeTime: string
} => {
  const [activeTime, setActiveTime] = React.useState<string>('00:00:00')
  const [isActivelyEngaging, setIsActivelyEngaging] = React.useState(false)
  const intervalRef = React.useRef<number | null>(null)
  const sessionStart = React.useRef<number | null>(null)
  const lastInteraction = React.useRef<number | null>(null)

  React.useEffect(() => {
    /**
     * Resets all timer state and clears localStorage
     */
    const resetTimer = (): void => {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }

      sessionStart.current = null
      lastInteraction.current = null
      setActiveTime('00:00:00')
      setIsActivelyEngaging(false)
      localStorage.removeItem(SESSION_TIME_KEY)
      localStorage.removeItem(LAST_ACTIVE_KEY)
    }

    /**
     * Rehydrates an existing session if it's still valid
     * @returns {boolean} Whether the session was successfully rehydrated
     */
    const rehydrateSession = (): boolean => {
      const storedSessionStart = localStorage.getItem(LAST_ACTIVE_KEY)
      const storedEngagedTime = localStorage.getItem(SESSION_TIME_KEY)

      if (!storedSessionStart || !storedEngagedTime) {
        return false
      }

      const parsedSessionStart = parseInt(storedSessionStart, 10)
      const now = Date.now()

      // Only rehydrate if the session is still within the inactivity threshold
      if (now - parsedSessionStart >= INACTIVITY_THRESHOLD_MS) {
        resetTimer()
        return false
      }

      sessionStart.current = parsedSessionStart
      lastInteraction.current = now

      const duration = intervalToDuration({
        start: parsedSessionStart,
        end: now,
      })
      const formatted = `${pad(duration.hours)}:${pad(duration.minutes)}:${pad(
        duration.seconds,
      )}`

      setActiveTime(formatted)
      setIsActivelyEngaging(
        parseInt(storedEngagedTime, 10) >= TARGET_ENGAGEMENT_MS,
      )
      startInterval()

      return true
    }

    /**
     * Starts the interval timer to update engagement time
     * Updates every second and checks for inactivity
     */
    const startInterval = (): void => {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current)
      }

      intervalRef.current = window.setInterval(() => {
        const now = Date.now()
        const last = lastInteraction.current
        const start = sessionStart.current
        if (!last || !start) return

        if (now < start || now - last >= INACTIVITY_THRESHOLD_MS) {
          resetTimer()
          return
        }

        const engagedTime = now - start

        const duration = intervalToDuration({ start, end: now })
        const formatted = `${pad(duration.hours)}:${pad(
          duration.minutes,
        )}:${pad(duration.seconds)}`

        setActiveTime(formatted)

        // Only show ActivelyEngaging if we've reached the target time
        setIsActivelyEngaging(engagedTime >= TARGET_ENGAGEMENT_MS)
        localStorage.setItem(SESSION_TIME_KEY, engagedTime.toString())

        // run this check once per second
      }, 1000)
    }

    /**
     * Registers a new user interaction
     * Starts the timer if this is the first interaction
     */
    const registerInteraction = (): void => {
      const now = Date.now()
      lastInteraction.current = now

      if (!sessionStart.current) {
        sessionStart.current = now
        localStorage.setItem(LAST_ACTIVE_KEY, now.toString())
        localStorage.setItem(SESSION_TIME_KEY, '0')
        startInterval()
      }
    }

    /**
     * Handles explicit engagement stop
     */
    const handleStopEngagement = (): void => {
      resetTimer()
    }

    // Check for existing session on mount
    rehydrateSession()

    window.addEventListener(ENGAGEMENT_START_EVENT, registerInteraction)
    window.addEventListener(ENGAGEMENT_STOP_EVENT, handleStopEngagement)

    return (): void => {
      window.removeEventListener(ENGAGEMENT_START_EVENT, registerInteraction)
      window.removeEventListener(ENGAGEMENT_STOP_EVENT, handleStopEngagement)
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current)
      }
    }
  }, [])

  return {
    isActivelyEngaging,
    activeTime,
  }
}
